import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

/* ─────────────────────────────────────────────────────────────────
   PERF FIXES APPLIED:
   1. CursorGlow — throttled mousemove with rAF, will-change, no filter on move
   2. Stardust — reduced 50→24 particles, CSS animation instead of Framer
   3. OrbitRings — CSS animation instead of Framer (GPU-composited)
   4. FloatingSnippets — CSS keyframes, no Framer overhead
   5. ScrambleText — clearInterval guard, no closure leak
   6. GlowOrbs — CSS animation, will-change: transform
   7. All motion.div with repeat:Infinity use CSS where possible
   8. GlowGrid — pure CSS, static div, zero JS
   9. Every animated el has will-change: transform or opacity
   10. No filter: blur() on moving elements (causes layer promotion storm)
───────────────────────────────────────────────────────────────── */

/* ── Cursor glow — rAF throttled, transform-only (GPU) ── */
function CursorGlow() {
  const ref = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (rafRef.current) return; // skip if frame already queued
      rafRef.current = requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.style.transform =
            `translate(${e.clientX - 200}px, ${e.clientY - 200}px)`;
        }
        rafRef.current = null;
      });
    };
    window.addEventListener("mousemove", move, { passive: true });
    return () => {
      window.removeEventListener("mousemove", move);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="hero-cursor-glow" ref={ref} style={{
      position: "fixed", top: 0, left: 0, pointerEvents: "none", zIndex: 0,
      width: 400, height: 400, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(124,58,237,0.13) 0%, transparent 70%)",
      willChange: "transform",
      /* NO filter:blur — that forces composite layer on every frame */
    }} />
  );
}

/* ── Static glow grid — pure CSS, zero JS ── */
function GlowGrid() {
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage:
        "linear-gradient(rgba(124,58,237,0.055) 1px, transparent 1px)," +
        "linear-gradient(90deg, rgba(124,58,237,0.055) 1px, transparent 1px)",
      backgroundSize: "60px 60px",
    }} />
  );
}

/* ── Orbit rings — CSS animation (compositor thread, zero JS per frame) ── */
function OrbitRings() {
  const rings = useMemo(() => [
    { r: 260, dur: "22s", color: "rgba(124,58,237,0.22)", dot: 8 },
    { r: 340, dur: "30s", color: "rgba(168,85,247,0.16)", dot: 6 },
    { r: 420, dur: "38s", color: "rgba(232,121,249,0.11)", dot: 5 },
  ], []);

  return (
    <>
      <style>{`
        @keyframes orbit { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <div className="hero-orbit-rings" style={{ position: "absolute", top: "50%", left: "50%", pointerEvents: "none", zIndex: 0 }}>
        {rings.map((ring, i) => (
          <div key={i} style={{
            position: "absolute",
            width: ring.r * 2, height: ring.r * 2,
            borderRadius: "50%", border: `1px solid ${ring.color}`,
            top: -ring.r, left: -ring.r,
            animation: `orbit ${ring.dur} linear infinite`,
            willChange: "transform",
          }}>
            <div style={{
              position: "absolute",
              width: ring.dot, height: ring.dot,
              borderRadius: "50%",
              background: ring.color.replace(/[\d.]+\)$/, "0.9)"),
              boxShadow: `0 0 10px ${ring.color}`,
              top: -ring.dot / 2, left: "50%",
              marginLeft: -ring.dot / 2,
            }} />
          </div>
        ))}
      </div>
    </>
  );
}

/* ── Stardust — CSS keyframes, 24 stars (was 50 Framer divs) ── */
const STARS = Array.from({ length: 24 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: 1 + Math.random() * 2,
  dur: `${2.5 + Math.random() * 3.5}s`,
  delay: `${Math.random() * 5}s`,
}));

function Stardust() {
  return (
    <>
      <style>{`
        @keyframes starPulse {
          0%,100% { opacity:0.12; transform:scale(1); }
          50%      { opacity:0.85; transform:scale(1.6); }
        }
      `}</style>
      {STARS.map(s => (
        <div key={s.id} style={{
          position: "absolute", left: `${s.x}%`, top: `${s.y}%`,
          width: s.size, height: s.size, borderRadius: "50%",
          background: "#c084fc",
          boxShadow: `0 0 ${s.size * 3}px #c084fc`,
          animation: `starPulse ${s.dur} ${s.delay} ease-in-out infinite`,
          willChange: "transform, opacity",
          pointerEvents: "none",
        }} />
      ))}
    </>
  );
}

/* ── Floating code snippets — CSS keyframes, no Framer ── */
const SNIPPETS = [
  { code: "const void = new NyxLabs();", left: "5%", top: "22%", delay: "0s", dur: "10s" },
  { code: "await darkness.compile();", left: "70%", top: "16%", delay: "2s", dur: "10s" },
  { code: "return power.unleash();", left: "7%", top: "74%", delay: "1s", dur: "10s" },
  { code: "deploy({ env: 'shadow' });", left: "68%", top: "76%", delay: "3s", dur: "10s" },
];

function FloatingSnippets() {
  return (
    <>
      <style>{`
        @keyframes snippetFloat {
          0%   { opacity:0;   transform:translateY(16px); }
          15%  { opacity:0.7; transform:translateY(0px);  }
          75%  { opacity:0.7; transform:translateY(0px);  }
          100% { opacity:0;   transform:translateY(-16px);}
        }
      `}</style>
      {SNIPPETS.map((s, i) => (
        <div key={i} className="hero-floating-snippet" style={{
          position: "absolute", left: s.left, top: s.top,
          fontFamily: "monospace", fontSize: 11,
          color: "rgba(168,85,247,0.72)",
          background: "rgba(124,58,237,0.08)",
          border: "1px solid rgba(124,58,237,0.2)", borderRadius: 8,
          padding: "8px 14px",
          backdropFilter: "blur(8px)",
          whiteSpace: "nowrap", pointerEvents: "none", zIndex: 0,
          animation: `snippetFloat ${s.dur} ${s.delay} ease-in-out infinite`,
          willChange: "transform, opacity",
        }}>{s.code}</div>
      ))}
    </>
  );
}

/* ── Ambient orbs — CSS animation, NO filter on animate (static blur) ── */
function AmbientOrbs() {
  return (
    <>
      <style>{`
        @keyframes orbPulse1 { 0%,100%{transform:scale(1);opacity:.5} 50%{transform:scale(1.1);opacity:.8} }
        @keyframes orbPulse2 { 0%,100%{transform:scale(1);opacity:.4} 50%{transform:scale(1.2);opacity:.7} }
        @keyframes orbPulse3 { 0%,100%{transform:scale(1);opacity:.3} 50%{transform:scale(1.15);opacity:.6} }
      `}</style>
      <div style={{
        position: "absolute", top: "10%", left: "5%", width: 600, height: 600,
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle,rgba(124,58,237,0.15) 0%,transparent 70%)",
        filter: "blur(60px)",
        animation: "orbPulse1 8s ease-in-out infinite",
        willChange: "transform,opacity",
      }} />
      <div style={{
        position: "absolute", bottom: "5%", right: "5%", width: 500, height: 500,
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle,rgba(232,121,249,0.12) 0%,transparent 70%)",
        filter: "blur(60px)",
        animation: "orbPulse2 10s 3s ease-in-out infinite",
        willChange: "transform,opacity",
      }} />
      <div style={{
        position: "absolute", top: "40%", right: "20%", width: 300, height: 300,
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle,rgba(244,63,94,0.08) 0%,transparent 70%)",
        filter: "blur(50px)",
        animation: "orbPulse3 12s 5s ease-in-out infinite",
        willChange: "transform,opacity",
      }} />
    </>
  );
}

/* ── Scramble text — memoized, no closure leak ── */
const WORDS = ["POWER", "IMPACT", "LEGACY", "VISION", "FUTURE"];
const CHARS = "░▒▓█@#$%&";

function ScrambleText() {
  const [display, setDisplay] = useState(WORDS[0]);
  const idxRef = useRef(0);
  const timerRef = useRef(null);
  const scramRef = useRef(null);

  const runScramble = useCallback((target) => {
    let iter = 0;
    clearInterval(scramRef.current);
    scramRef.current = setInterval(() => {
      setDisplay(
        target.split("").map((ch, i) =>
          i < iter ? ch : CHARS[Math.floor(Math.random() * CHARS.length)]
        ).join("")
      );
      if (iter >= target.length) {
        clearInterval(scramRef.current);
        setDisplay(target);
      }
      iter += 0.45;
    }, 38);
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      idxRef.current = (idxRef.current + 1) % WORDS.length;
      runScramble(WORDS[idxRef.current]);
    }, 2600);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(scramRef.current);
    };
  }, [runScramble]);

  return (
    <span style={{
      background: "linear-gradient(135deg,#a855f7,#e879f9,#f43f5e)",
      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
      display: "inline-block", minWidth: "6ch",
    }}>{display}</span>
  );
}

/* ── Badge pulse — CSS animation ── */
function BadgeDot() {
  return (
    <>
      <style>{`@keyframes dotPulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
      <span style={{
        width: 7, height: 7, borderRadius: "50%", background: "#a855f7",
        boxShadow: "0 0 10px #a855f7", display: "block",
        animation: "dotPulse 1.5s ease-in-out infinite",
        willChange: "opacity",
      }} />
    </>
  );
}

/* ── Shimmer divider — CSS animation ── */
function ShimmerDivider() {
  return (
    <>
      <style>{`
        @keyframes shimmerSlide { 0%{background-position:0% 0%} 100%{background-position:200% 0%} }
      `}</style>
      <div style={{
        width: 80, height: 2, margin: "0 auto 28px",
        background: "linear-gradient(90deg,transparent,#7c3aed,#e879f9,#f43f5e,transparent)",
        backgroundSize: "200% 100%",
        animation: "shimmerSlide 3s linear infinite",
        willChange: "background-position",
      }} />
    </>
  );
}

/* ── Scroll indicator — CSS animation ── */
function ScrollIndicator() {
  return (
    <>
      <style>{`
        @keyframes scrollBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(10px)}}
        @keyframes lineBreath{0%,100%{height:24px}50%{height:40px}}
      `}</style>
      <div style={{ marginTop: 80 }}>
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          animation: "scrollBounce 2s ease-in-out infinite", willChange: "transform"
        }}>
          <span style={{
            fontFamily: "'DM Sans',sans-serif", fontSize: 10,
            letterSpacing: "0.2em", color: "rgba(241,240,245,0.25)", textTransform: "uppercase"
          }}>
            Scroll to explore
          </span>
          <div style={{
            width: 1, background: "linear-gradient(to bottom,rgba(168,85,247,0.6),transparent)",
            animation: "lineBreath 2s ease-in-out infinite", willChange: "height",
          }} />
        </div>
      </div>
    </>
  );
}

/* ════════════ HERO ════════════ */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.15 } } };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } } };

export default function Hero() {
  const navigate = useNavigate();

  return (
    <div className="hero-page" style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden", paddingTop: 80,
    }}>
      {/* Layer 0 — static/CSS-only background elements */}
      <CursorGlow />
      <GlowGrid />
      <AmbientOrbs />
      <Stardust />
      <FloatingSnippets />
      <OrbitRings />

      {/* Layer 1 — Framer Motion for entrance animations only (not looping) */}
      <motion.div
        className="hero-content-wrap"
        initial="hidden" animate="visible" variants={stagger}
        style={{ textAlign: "center", maxWidth: 900, padding: "0 24px", position: "relative", zIndex: 2 }}
      >
        {/* Badge */}
        <motion.div variants={fadeUp}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "7px 18px", borderRadius: 100,
            background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.35)",
            marginBottom: 36,
          }}>
            <BadgeDot />
            <span style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 600,
              color: "#c084fc", letterSpacing: "0.12em", textTransform: "uppercase"
            }}>
              Dark Innovation Lab
            </span>
          </div>
        </motion.div>

        {/* Heading */}
        <motion.h1 variants={fadeUp} style={{
          fontFamily: "'Syne',sans-serif", fontWeight: 800,
          fontSize: "clamp(50px,9vw,100px)", lineHeight: 1.0, letterSpacing: "-0.03em",
          color: "#f1f0f5", marginBottom: 24,
        }}>
          Silence Becomes<br />
          <ScrambleText />
        </motion.h1>

        {/* Divider */}
        <motion.div variants={{ hidden: { scaleX: 0 }, visible: { scaleX: 1, transition: { duration: 0.8 } } }}>
          <ShimmerDivider />
        </motion.div>

        {/* Subtitle */}
        <motion.p variants={fadeUp} style={{
          fontFamily: "'DM Sans',sans-serif", fontWeight: 300,
          fontSize: "clamp(15px,2vw,19px)", lineHeight: 1.8,
          color: "rgba(241,240,245,0.5)", maxWidth: 580, margin: "0 auto 52px",
        }}>
          Code flows where the world sleeps. We engineer digital experiences that
          breathe in darkness — precision-built, impact-ready.
        </motion.p>

        {/* CTAs */}
        <motion.div variants={fadeUp}
          className="hero-cta-row"
          style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 50px rgba(124,58,237,0.7)" }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/about")}
            style={{
              padding: "15px 40px", borderRadius: 14, cursor: "pointer",
              background: "linear-gradient(135deg,#7c3aed,#a855f7)",
              border: "none", color: "#fff",
              fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 15,
              letterSpacing: "0.03em", boxShadow: "0 0 30px rgba(124,58,237,0.5)",
            }}>
            Enter the Void →
          </motion.button>
          <motion.button
            whileHover={{
              scale: 1.05, borderColor: "rgba(168,85,247,0.6)", color: "#c084fc",
              boxShadow: "0 0 30px rgba(124,58,237,0.2)"
            }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/services")}
            style={{
              padding: "15px 40px", borderRadius: 14, cursor: "pointer",
              background: "transparent",
              border: "1px solid rgba(124,58,237,0.35)", color: "rgba(241,240,245,0.75)",
              fontFamily: "'DM Sans',sans-serif", fontWeight: 500, fontSize: 15,
              transition: "all 0.3s ease",
            }}>
            Explore Services
          </motion.button>
        </motion.div>

        {/* Scroll */}
        <motion.div variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 1 } } }}>
          <ScrollIndicator />
        </motion.div>
      </motion.div>
      <style>{`
        @media (max-width: 768px) {
          .hero-page { padding-top: 96px !important; }
          .hero-content-wrap { padding: 0 20px !important; }
          .hero-cursor-glow, .hero-orbit-rings, .hero-floating-snippet { display: none !important; }
          .hero-cta-row button { width: 100%; }
        }
        @media (max-width: 420px) {
          .hero-page { padding-top: 88px !important; }
          .hero-content-wrap { padding: 0 14px !important; }
        }
      `}</style>
    </div>
  );
}
